import { TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { RadioButton } from "@/components/RadioButton"
import { NIL_UUID } from "@/domain/resources/constants"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { getSharedEntityType } from "@/domain/sharedData/getSharedUtils"
import { useControlledRadioProps } from "@/hooks/useControlledRadioProps"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type SharedDataJsonldReadCollectionSharedData,
  type SharedDataPatchSharedDataInputWriteItemSharedData,
  getGetUserSharedDataQueryKey,
  getGetUserSharedDatasQueryKey,
  useUpdateUserSharedData,
} from "@/networking/api"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { RequiredErrorMessage } from "../RequiredErrorMessage"
import SharedEntityName from "./SharedEntityName"
import { SHARING_ABORT_MOTIVATION } from "./types"

type FormType = Partial<SharedDataPatchSharedDataInputWriteItemSharedData>

type Props = {
  isOpen: boolean
  onClose: () => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
}

export default function ShareEndShareModal({ isOpen, onClose, sharedEntity }: Props) {
  const entityType = getSharedEntityType(sharedEntity)

  const defaultValues: FormType = {
    messageAnswer: "",
    subjectAnswer: SHARING_ABORT_MOTIVATION.DONE_BY_OTHER as string,
  }

  const modalForm = useForm<FormType>({
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = modalForm

  const accessAbortMotivationOptions = [
    {
      value: SHARING_ABORT_MOTIVATION.DONE_BY_OTHER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.DONE_BY_OTHER}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.ABORTED_BY_CUSTOMER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.ABORTED_BY_CUSTOMER}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.PROJECT_NOT_POSSIBLE,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.PROJECT_NOT_POSSIBLE}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.NO_ARRANGEMENT_FOUND,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.NO_ARRANGEMENT_FOUND}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.OTHER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.OTHER}`,
    },
  ]

  const { getRadioProps: getMotivationRadioProps } = useControlledRadioProps({
    control,
    name: "subjectAnswer",
    defaultValue: defaultValues.subjectAnswer,
    rules: { required: true },
  })

  const { mutate, isPending } = useUpdateUserSharedData()
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()

  const postShareRequest = (formData: FormType) => {
    const data: SharedDataPatchSharedDataInputWriteItemSharedData = {
      ...formData,
      id: sharedEntity.id ?? NIL_UUID,
      status: SHARED_DATA_STATUS.CLOSED,
    }

    mutate(
      {
        data,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDataQueryKey(sharedEntity.id),
          })
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDatasQueryKey(),
          })
          onClose()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id="userSharedData.end_ko" />}
      scrollBehavior="outside"
    >
      <form onSubmit={handleSubmit(postShareRequest)}>
        <ModalBody>
          <Flex flexDir="column" gap="6">
            <Box>
              <FormattedMessage id="userSharedData.end_ko.message" />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.object" />
              </FormLabel>
              <SharedEntityName item={sharedEntity} displayInputLike={true} />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.motivation" />
              </FormLabel>
              <FormControl isInvalid={!!errors.subjectAnswer}>
                <Flex justify="space-between">
                  {accessAbortMotivationOptions.map(subject => {
                    return (
                      <RadioButton
                        key={subject.value}
                        labelTranslationId={subject.translationId}
                        {...getMotivationRadioProps({ value: subject.value })}
                      />
                    )
                  })}
                </Flex>
                <RequiredErrorMessage />
              </FormControl>
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.comment" />
              </FormLabel>
              <Textarea {...register("messageAnswer")} variant="outlineWithBorder" />
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button display="flex" gap="2" type="submit" isLoading={isPending}>
            <FormattedMessage id="confirm" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
